import React, { useState, useCallback, useEffect } from 'react';
import { budgetsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  Download, 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  Info,
  Loader2,
  History
} from 'lucide-react';

const MONTHS = [
  { key: 'jul', label: 'July' },
  { key: 'aug', label: 'August' },
  { key: 'sep', label: 'September' },
  { key: 'oct', label: 'October' },
  { key: 'nov', label: 'November' },
  { key: 'dec', label: 'December' },
  { key: 'jan', label: 'January' },
  { key: 'feb', label: 'February' },
  { key: 'mar', label: 'March' },
  { key: 'apr', label: 'April' },
  { key: 'may', label: 'May' },
  { key: 'jun', label: 'June' },
];

const ImportActualsModal = ({ 
  isOpen, 
  onClose, 
  budget, 
  onImportSuccess 
}) => {
  const { isDark } = useTheme();
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('incremental');
  const [selectedMonths, setSelectedMonths] = useState(MONTHS.map(m => m.key));
  const [selectAllMonths, setSelectAllMonths] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (isOpen && budget?.id) {
      loadImportHistory();
    }
  }, [isOpen, budget?.id]);

  const loadImportHistory = async () => {
    if (!budget?.id) return;
    try {
      setLoadingHistory(true);
      const res = await budgetsApi.getActualsImportHistory(budget.id);
      setImportHistory(res.data.history || []);
    } catch (error) {
      console.error('Failed to load import history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = selectedFile.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValid) {
      toast.error('Please select an Excel (.xlsx, .xls) or CSV (.csv) file');
      return;
    }
    
    setFile(selectedFile);
    setImportResult(null);
  };

  const handleMonthToggle = (monthKey) => {
    setSelectedMonths(prev => {
      if (prev.includes(monthKey)) {
        return prev.filter(m => m !== monthKey);
      }
      return [...prev, monthKey];
    });
    setSelectAllMonths(false);
  };

  const handleSelectAllMonths = (checked) => {
    setSelectAllMonths(checked);
    if (checked) {
      setSelectedMonths(MONTHS.map(m => m.key));
    } else {
      setSelectedMonths([]);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file to import');
      return;
    }
    
    if (selectedMonths.length === 0) {
      toast.error('Please select at least one month to import');
      return;
    }
    
    setIsUploading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_mode', importMode);
      formData.append('months_to_import', selectedMonths.join(','));
      
      const res = await budgetsApi.importActuals(budget.id, formData);
      
      setImportResult({
        success: true,
        ...res.data.details
      });
      
      toast.success(`Actuals imported successfully! ${res.data.details.items_updated} items updated.`);
      
      // Refresh history
      loadImportHistory();
      
      // Callback to parent
      if (onImportSuccess) {
        onImportSuccess();
      }
      
    } catch (error) {
      console.error('Import failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to import actuals';
      setImportResult({
        success: false,
        error: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await budgetsApi.getActualsTemplate(budget.id);
      
      // Create download link
      const blob = new Blob([res.data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setShowHistory(false);
    onClose();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`max-w-2xl ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            <Upload className="w-5 h-5 text-blue-500" />
            Import Actuals
          </DialogTitle>
          <DialogDescription className={isDark ? 'text-zinc-400' : ''}>
            Import actual financial data for <span className="font-medium">{budget?.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Toggle between Import and History */}
          <div className="flex gap-2">
            <Button
              variant={!showHistory ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowHistory(false)}
              className="rounded-sm"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button
              variant={showHistory ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowHistory(true)}
              className="rounded-sm"
            >
              <History className="w-4 h-4 mr-2" />
              Import History ({importHistory.length})
            </Button>
          </div>

          {!showHistory ? (
            <>
              {/* File Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive 
                    ? isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'
                    : file 
                      ? isDark ? 'border-emerald-500 bg-emerald-900/20' : 'border-emerald-500 bg-emerald-50'
                      : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-300 hover:border-slate-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                data-testid="actuals-file-dropzone"
              >
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className={`w-8 h-8 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <div className="text-left">
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{file.name}</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFile(null)}
                      className={`ml-auto ${isDark ? 'text-zinc-400 hover:text-white' : ''}`}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} />
                    <p className={isDark ? 'text-zinc-300' : 'text-slate-600'}>
                      Drag & drop your file here, or{' '}
                      <label className="text-blue-500 cursor-pointer hover:underline">
                        browse
                        <input
                          type="file"
                          className="hidden"
                          accept=".xlsx,.xls,.csv"
                          onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                        />
                      </label>
                    </p>
                    <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                      Supports Excel (.xlsx, .xls) and CSV (.csv)
                    </p>
                  </>
                )}
              </div>

              {/* Download Template Link */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2">
                  <Info className={`w-4 h-4 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`} />
                  <span className={`text-sm ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                    Need a template? Download pre-populated with account codes
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                  data-testid="download-actuals-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </div>

              {/* Import Mode */}
              <div className="space-y-3">
                <Label className={isDark ? 'text-zinc-300' : ''}>Import Mode</Label>
                <RadioGroup value={importMode} onValueChange={setImportMode} className="grid grid-cols-2 gap-3">
                  <div className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer ${
                    importMode === 'incremental' 
                      ? isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'
                      : isDark ? 'border-zinc-700' : 'border-slate-200'
                  }`} onClick={() => setImportMode('incremental')}>
                    <RadioGroupItem value="incremental" id="incremental" className="mt-1" />
                    <div>
                      <Label htmlFor="incremental" className={`font-medium cursor-pointer ${isDark ? 'text-white' : ''}`}>
                        Incremental
                      </Label>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Only update months with non-zero values. Preserves existing data.
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer ${
                    importMode === 'overwrite' 
                      ? isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'
                      : isDark ? 'border-zinc-700' : 'border-slate-200'
                  }`} onClick={() => setImportMode('overwrite')}>
                    <RadioGroupItem value="overwrite" id="overwrite" className="mt-1" />
                    <div>
                      <Label htmlFor="overwrite" className={`font-medium cursor-pointer ${isDark ? 'text-white' : ''}`}>
                        Overwrite
                      </Label>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Replace all values for selected months, including zeros.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Months Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className={isDark ? 'text-zinc-300' : ''}>Months to Import</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="select-all-months" 
                      checked={selectAllMonths} 
                      onCheckedChange={handleSelectAllMonths}
                    />
                    <Label htmlFor="select-all-months" className={`text-sm cursor-pointer ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      Select All
                    </Label>
                  </div>
                </div>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {MONTHS.map(({ key, label }) => (
                    <div
                      key={key}
                      onClick={() => handleMonthToggle(key)}
                      className={`flex items-center justify-center p-2 rounded-md border cursor-pointer transition-colors text-sm ${
                        selectedMonths.includes(key)
                          ? isDark ? 'border-blue-500 bg-blue-900/30 text-blue-400' : 'border-blue-500 bg-blue-50 text-blue-700'
                          : isDark ? 'border-zinc-700 text-zinc-400 hover:border-zinc-600' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {label.slice(0, 3)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Import Result */}
              {importResult && (
                <div className={`p-4 rounded-lg ${
                  importResult.success 
                    ? isDark ? 'bg-emerald-900/20 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'
                    : isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {importResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${
                        importResult.success 
                          ? isDark ? 'text-emerald-400' : 'text-emerald-700'
                          : isDark ? 'text-red-400' : 'text-red-700'
                      }`}>
                        {importResult.success ? 'Import Successful' : 'Import Failed'}
                      </p>
                      {importResult.success ? (
                        <div className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                          <p>Updated {importResult.items_updated} line items</p>
                          {importResult.months_with_data?.length > 0 && (
                            <p className="mt-1">
                              Months updated: {importResult.months_with_data.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {importResult.error}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Import History View */
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : importHistory.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No import history yet</p>
                </div>
              ) : (
                importHistory.map((record) => (
                  <div 
                    key={record.id}
                    className={`p-3 rounded-lg border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {record.filename}
                        </p>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                          {formatDate(record.imported_at)} by {record.imported_by}
                        </p>
                      </div>
                      <Badge variant="outline" className={`text-xs ${
                        record.status === 'success' 
                          ? 'border-emerald-500 text-emerald-500' 
                          : 'border-red-500 text-red-500'
                      }`}>
                        {record.status}
                      </Badge>
                    </div>
                    <div className={`flex flex-wrap gap-2 mt-2 text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-zinc-700' : 'bg-slate-200'}`}>
                        {record.import_mode}
                      </span>
                      <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-zinc-700' : 'bg-slate-200'}`}>
                        {record.items_updated} items
                      </span>
                      {record.months_imported?.length > 0 && (
                        <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-zinc-700' : 'bg-slate-200'}`}>
                          {record.months_imported.length} months
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
          >
            {importResult?.success ? 'Close' : 'Cancel'}
          </Button>
          {!showHistory && (
            <Button 
              onClick={handleImport} 
              disabled={!file || isUploading || selectedMonths.length === 0}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
              data-testid="import-actuals-submit-btn"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Actuals
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportActualsModal;
